import { computed, getCurrentInstance, ref, watch } from 'vue';
import { getActivePinia, type Pinia } from 'pinia';
import type { UserSettingsRecord } from '@/types/assigning';

export type FirstDayOfWeek = 'SUNDAY' | 'MONDAY';

// Read the reactive state without making shared UI controls initialize API stores.
export function getUserPreferences(): UserSettingsRecord {
	const instance = getCurrentInstance();
	const pinia = instance ? (instance.appContext.config.globalProperties['$pinia'] as Pinia | undefined) : getActivePinia();
	const state = pinia?.state.value['userSettingsStore'] as { userSettings?: UserSettingsRecord } | undefined;
	return state?.userSettings ?? {};
}

export function validPageSize(value: unknown): number | undefined {
	const size = Number(value);
	return Number.isSafeInteger(size) && size > 0 ? size : undefined;
}

export function validFirstDayOfWeek(value: unknown): FirstDayOfWeek | undefined {
	const day = typeof value === 'string' ? value.trim().toUpperCase() : '';
	return day === 'SUNDAY' || day === 'MONDAY' ? day : undefined;
}

export function userFirstDayOfWeek(): FirstDayOfWeek {
	const preference = validFirstDayOfWeek(getUserPreferences().firstDayOfWeek);
	if (preference) return preference;
	try {
		const cached = JSON.parse(localStorage.getItem('user_settings') || 'null') as UserSettingsRecord | null;
		return validFirstDayOfWeek(cached?.firstDayOfWeek) ?? 'SUNDAY';
	} catch {
		return 'SUNDAY';
	}
}

export function useUserPageSize(fallback = 10, onPreferenceChange?: (size: number) => void) {
	const preferredSize = computed(() => validPageSize(getUserPreferences().pageSize));
	const rows = ref(preferredSize.value ?? fallback);
	watch(preferredSize, (size) => {
		rows.value = size ?? fallback;
		onPreferenceChange?.(rows.value);
	});
	return rows;
}
